import { supabase } from '@/lib/supabase';
import type { DesignerProfileRow } from '@/types/database';
import type { Branding } from '@/lib/branding';

const BUCKET = 'branding';

/**
 * The signed-in person's FIRM branding, or null if it's never been saved.
 *
 * No designer_id filter: branding is one row per firm (0030), and the rules
 * return only the caller's firm's row. Filtering by the caller's own id would
 * show every member except whoever saved it an empty profile.
 */
export async function getMyProfile(): Promise<DesignerProfileRow | null> {
  const { data: session } = await supabase.auth.getUser();
  if (!session.user?.id) return null;
  const { data, error } = await supabase
    .from('designer_profiles')
    .select('*')
    .maybeSingle();
  if (error) throw error;
  return data ?? null;
}

/**
 * Create-or-update. The row is made lazily on first save. Conflicts on the
 * FIRM, so a second owner updates the firm's branding rather than trying to
 * add another row. Only an owner may save; the rules refuse anyone else.
 */
export async function saveMyProfile(patch: {
  company_name?: string | null;
  tagline?: string | null;
  logo_storage_path?: string | null;
  /** 0045 — the designer's photo, the name's typeface and its color. */
  photo_storage_path?: string | null;
  brand_font?: string | null;
  brand_color?: string | null;
}): Promise<void> {
  const { data: session } = await supabase.auth.getUser();
  const designerId = session.user?.id;
  if (!designerId) throw new Error('Not signed in');
  const { error } = await supabase
    .from('designer_profiles')
    .upsert({ designer_id: designerId, ...patch }, { onConflict: 'firm_id' });
  if (error) throw error;
}

/**
 * Upload a brand image — the firm's logo, or the designer's photo — and return
 * its storage path.
 *
 * Path is `<designer_id>/<uuid>.<ext>` — the storage policy keeps a designer
 * inside their own folder, and a fresh uuid per upload sidesteps CDN caching
 * of a replaced image. Both kinds share the folder and the rules; the column
 * they are saved to is what makes one a logo and the other a face.
 */
export async function uploadBrandImage(file: File): Promise<string> {
  const { data: session } = await supabase.auth.getUser();
  const designerId = session.user?.id;
  if (!designerId) throw new Error('Not signed in');

  // split('.').pop() on a name with no dot returns the whole name, so a file
  // called "logo" would upload as ".logo". Look for a real extension instead.
  const dot = file.name.lastIndexOf('.');
  const raw = dot > 0 ? file.name.slice(dot + 1) : '';
  const ext = raw.toLowerCase().replace(/[^a-z0-9]/g, '') || 'png';
  const path = `${designerId}/${crypto.randomUUID()}.${ext}`;
  const { error } = await supabase.storage.from(BUCKET).upload(path, file, { upsert: false });
  if (error) throw error;
  return path;
}

/** Public URL for a logo. The branding bucket is public by design. */
export function logoUrl(path: string): string {
  return supabase.storage.from(BUCKET).getPublicUrl(path).data.publicUrl;
}

/** Branding for a client share portal — anonymous, gated on the share token. */
export async function fetchShareBranding(token: string): Promise<Branding | null> {
  const { data, error } = await supabase.rpc('get_share_branding', { token });
  if (error) throw error;
  return (data as Branding | null) ?? null;
}
